import { GridColDef } from '@mui/x-data-grid';
import { getDateUsingFormat } from 'wisestep-base';

import { isValueMissing, renderCellFor, RTRAnalyticsWidget } from '../../helper';
import { IPostgreRTRDTO } from '../types';

export const getColumnsForRTR = (
  widgetName: RTRAnalyticsWidget,
  widgetData: IPostgreRTRDTO
): GridColDef[] => {
  const uniqueStatus = Array.from(
    new Set(widgetData?.resultSet?.flatMap((record) => Object.keys(record?.statusWithCount ?? '')))
  );
  switch (widgetName) {
    case RTRAnalyticsWidget.RTR_CANDIDATE_TABLE:
      return [
        {
          field: 'candidateName',
          headerName: 'Candidate Name',
          minWidth: 160,
          flex: 2,
          renderCell: (params) => renderCellFor('CANDIDATE', params),
        },
        {
          field: 'jobTitle',
          headerName: 'Job Title (Job Code)',
          minWidth: 300,
          flex: 1,
          renderCell: (param) =>
            renderCellFor('JOB', param, `${param.row.jobTitle} (${param.row.jobCode})`),
        },
        {
          field: 'recruiterName',
          headerName: 'Recruiter Name',
          flex: 2,
          minWidth: 180,
          renderCell: (params) =>
            renderCellFor(
              'RECRUITER',
              params,
              isValueMissing(params.row['recruiterName'])
                ? params.row['recruiterEmail']
                : params.row['recruiterName']
            ),
        },
        {
          field: 'clientName',
          headerName: 'Client Name',
          minWidth: 120,
          flex: 2,
          renderCell: (params) => renderCellFor('CLIENT', params),
        },
        {
          field: 'accountManagerName',
          headerName: 'Account Manager',
          minWidth: 160,
          flex: 2,
          renderCell: (params) => renderCellFor('ACCOUNT_MANAGER', params),
        },
        {
          field: 'requestedOn',
          headerName: 'Requested On',
          minWidth: 120,
          flex: 2,
          renderCell: (params) =>
            params.value ? getDateUsingFormat(params.value, 'YYYY-MM-DD') : '',
        },
        {
          field: 'submittedOn',
          headerName: 'Submitted On',
          minWidth: 120,
          flex: 2,
          renderCell: (params) =>
            params.value ? getDateUsingFormat(params.value, 'YYYY-MM-DD') : '',
        },
        {
          field: 'status',
          headerName: 'Status',
          minWidth: 120,
          flex: 2,
        },
      ];
    case RTRAnalyticsWidget.RTR_RECRUITER_TABLE:
      return [
        {
          field: 'recruiterName',
          headerName: 'Recruiter Name',
          flex: 2,
          minWidth: 170,
          renderCell: (params) =>
            renderCellFor(
              'RECRUITER',
              params,
              isValueMissing(params.row['recruiterName'])
                ? params.row['recruiterEmail']
                : params.row['recruiterName']
            ),
        },
        {
          field: 'count',
          headerName: `Total Count(${widgetData?.count ?? 0})`,
          flex: 1,
          minWidth: 150,
        },
        ...uniqueStatus.map((status) => ({
          field: status,
          headerName: status || 'N/A',
          width: 150,
        })),
      ];
    case RTRAnalyticsWidget.RTR_JOB_TITLE_TABLE:
      return [
        {
          field: 'jobTitle',
          headerName: 'Job Title',
          flex: 2,
          minWidth: 170,
          renderCell: (params) => renderCellFor('JOB', params),
        },
        {
          field: 'count',
          headerName: `Total Count(${widgetData?.count ?? 0})`,
          flex: 1,
          minWidth: 150,
        },
        ...uniqueStatus.map((status) => ({
          field: status,
          headerName: status || 'N/A',
          width: 150,
        })),
      ];
    default:
      throw new Error('Invalid table widget name');
  }
};

export function getTableDataForRTR(widgetName: RTRAnalyticsWidget, widgetData: IPostgreRTRDTO) {
  switch (widgetName) {
    case RTRAnalyticsWidget.RTR_RECRUITER_TABLE:
    case RTRAnalyticsWidget.RTR_JOB_TITLE_TABLE:
      {
        const uniqueStatus = Array.from(
          new Set(
            widgetData.resultSet?.flatMap((record) => Object.keys(record.statusWithCount ?? {}))
          )
        );
        widgetData.resultSet?.forEach((record, index) => {
          record.id = index;
          uniqueStatus.forEach((status) => {
            record[status] = record.statusWithCount?.[status] || 0;
          });
        });
      }
      break;
    default:
      widgetData.resultSet?.forEach((record, index) => (record.id = index));
  }

  return widgetData.resultSet;
}
